import React, { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { getAllJobs } from '../utils/careers'
import VersionBadge from '../components/VersionBadge'
import { usePageSeo } from '../hooks/usePageSeo'
import {
  absoluteUrl,
  getOrganizationSchemaId,
  getSiteUrl,
  getWebsiteSchemaId
} from '../lib/siteUrl'
import {
  SITEMAP_HOME_SECTION_HASHES,
  SITEMAP_MAIN_PATHS
} from '../lib/sitemapUrls'

function defaultLoc (path) {
  const site = getSiteUrl()
  if (path === '/') return `${site}/`
  return `${site}${path}`
}

function buildItemListElements (teamMembers, jobs) {
  let position = 1
  const elements = []

  for (const path of SITEMAP_MAIN_PATHS) {
    elements.push({
      '@type': 'ListItem',
      position: position++,
      url: defaultLoc(path)
    })
  }

  for (const hash of SITEMAP_HOME_SECTION_HASHES) {
    elements.push({
      '@type': 'ListItem',
      position: position++,
      url: `${defaultLoc('/')}#${hash}`
    })
  }

  for (const member of teamMembers) {
    const slug =
      member.slug ||
      (member.name || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
    if (slug) {
      elements.push({
        '@type': 'ListItem',
        position: position++,
        name: member.name,
        url: `${getSiteUrl()}/team/${slug}`
      })
    }
  }

  for (const job of jobs) {
    if (job?.slug) {
      elements.push({
        '@type': 'ListItem',
        position: position++,
        name: job.title || job.slug,
        url: `${getSiteUrl()}/careers/${job.slug}`
      })
    }
  }

  return elements
}

function buildJsonLdGraph ({
  t,
  language,
  canonicalSitemap,
  jobs,
  teamMembers
}) {
  const itemListElement = buildItemListElements(teamMembers, jobs)

  const inLangPage = language === 'pt' ? 'pt-BR' : 'en-US'
  const site = getSiteUrl()
  const webId = getWebsiteSchemaId()
  const orgId = getOrganizationSchemaId()

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': webId,
        url: site,
        name: 'VIVA Psychotherapy',
        description: t('sitemap.structuredData.description'),
        inLanguage: ['en-US', 'pt-BR'],
        publisher: { '@id': orgId }
      },
      {
        '@type': ['MedicalBusiness', 'ProfessionalService'],
        '@id': orgId,
        name: 'VIVA Psychotherapy',
        description: t('sitemap.structuredData.description'),
        url: site,
        telephone: '+1-561-759-7519',
        email: 'info@vivapsychotherapy.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '5301 North Federal Highway Suite 200',
          addressLocality: 'Boca Raton',
          addressRegion: 'FL',
          postalCode: '33487',
          addressCountry: 'US'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '26.3906',
          longitude: '-80.0887'
        },
        areaServed: [
          { '@type': 'City', name: 'Boca Raton' },
          { '@type': 'City', name: 'Delray Beach' },
          { '@type': 'City', name: 'Deerfield Beach' },
          { '@type': 'City', name: 'Boynton Beach' },
          { '@type': 'City', name: 'West Palm Beach' },
          { '@type': 'City', name: 'Pompano Beach' },
          { '@type': 'City', name: 'Coral Springs' },
          { '@type': 'State', name: 'Florida' },
          {
            '@type': 'GeoCircle',
            geoMidpoint: {
              '@type': 'GeoCoordinates',
              latitude: '26.3683',
              longitude: '-80.1289'
            },
            description: '30-mile radius from Boca Raton, FL'
          }
        ],
        serviceType: [
          'Individual Therapy',
          'Group Therapy',
          'Family Counseling',
          'Trauma Therapy',
          'Mental Health Counseling'
        ],
        priceRange: '$$',
        paymentAccepted: ['Cash', 'Credit Card', 'Insurance', 'HSA', 'FSA'],
        currenciesAccepted: 'USD',
        availableLanguage: ['English', 'Portuguese'],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61578353949539',
          'https://www.instagram.com/vivapsychotherapy',
          'https://www.psychologytoday.com/us/therapists/lidiana-cartolano-boca-raton-fl/1038382'
        ]
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalSitemap}#webpage`,
        url: canonicalSitemap,
        name: `${t('sitemap.meta.title')} | VIVA Psychotherapy`,
        description: t('sitemap.meta.description'),
        isPartOf: { '@id': webId },
        about: { '@id': orgId },
        inLanguage: inLangPage,
        dateModified: new Date().toISOString().slice(0, 10),
        isAccessibleForFree: true
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalSitemap}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: t('sitemap.links.home'),
            item: defaultLoc('/')
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: t('sitemap.links.sitemapPage'),
            item: canonicalSitemap
          }
        ]
      },
      {
        '@type': 'ItemList',
        '@id': `${canonicalSitemap}#sitemap-index`,
        name: 'VIVA Psychotherapy site index',
        description:
          'Primary public URLs for search engines, AI crawlers, and assistants. Staff scheduling under /scheduler/ is excluded.',
        numberOfItems: itemListElement.length,
        itemListElement
      }
    ]
  }
}

const Sitemap = () => {
  const t = useTranslation()
  const { language, isLoading, translations } = useLanguage()
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)

  useEffect(() => {
    loadJobs()
  }, [])



  const loadJobs = async () => {
    try {
      setJobsLoading(true)
      const allJobs = await getAllJobs()
      setJobs(allJobs)
    } catch (error) {
      console.error('Error loading jobs:', error)
    } finally {
      setJobsLoading(false)
    }
  }

  const langPrefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''

  const location = useLocation()
  const teamMembers = t('home.team.members') || []
  const teamMembersKey = teamMembers.map((m) => m.slug || m.name).join('|')

  usePageSeo({
    enabled: !isLoading,
    title: `${t('sitemap.meta.title')} | VIVA Psychotherapy`,
    description: t('sitemap.meta.description'),
    keywords: t('sitemap.meta.keywords'),
    pathname: location.pathname
  })

  useEffect(() => {
    if (isLoading) return undefined

    const canonicalSitemap = absoluteUrl(location.pathname)

    let sitemapLink = document.querySelector('link[rel="sitemap"][data-sitemap-aux]')
    if (!sitemapLink) {
      sitemapLink = document.createElement('link')
      sitemapLink.setAttribute('rel', 'sitemap')
      sitemapLink.setAttribute('type', 'application/xml')
      sitemapLink.setAttribute('data-sitemap-aux', '')
      document.head.appendChild(sitemapLink)
    }
    sitemapLink.setAttribute('href', `${getSiteUrl()}/sitemap.xml`)
    sitemapLink.setAttribute('title', 'XML Sitemap')

    const graph = buildJsonLdGraph({
      t,
      language,
      canonicalSitemap,
      jobs,
      teamMembers
    })

    let structuredDataScript = document.querySelector(
      'script[type="application/ld+json"][data-sitemap-graph]'
    )
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script')
      structuredDataScript.setAttribute('type', 'application/ld+json')
      structuredDataScript.setAttribute('data-sitemap-graph', '')
      document.head.appendChild(structuredDataScript)
    }
    structuredDataScript.textContent = JSON.stringify(graph)

    return () => {
      document.querySelectorAll('[data-sitemap-aux], [data-sitemap-graph]').forEach((el) => {
        el.remove()
      })
    }
  }, [isLoading, language, location.pathname, t, jobs, teamMembersKey])

  const homePathname = langPrefix || '/'
  const site = getSiteUrl()

  const pageUrl = (path) => {
    if (path === '/') return `${site}${langPrefix || '/'}`.replace(/\/\/$/, '/')
    return `${site}${langPrefix}${path}`
  }

  // Main navigation links (paths match App routes; langPrefix is /pt or /en)
  const mainPages = [
    { path: '/', label: t('sitemap.links.home'), description: t('sitemap.links.homeDesc') },
    { path: '/about', label: t('sitemap.links.about'), description: t('sitemap.links.aboutDesc') },
    { path: '/team', label: t('sitemap.links.team'), description: t('sitemap.links.teamDesc') },
    { path: '/contact', label: t('sitemap.links.contact'), description: t('sitemap.links.contactDesc') },
    { path: '/services', label: t('sitemap.links.services'), description: t('sitemap.links.servicesDesc') },
    { path: '/therapy-modality', label: t('sitemap.links.therapyModality'), description: t('sitemap.links.therapyModalityDesc') },
    { path: '/resources', label: t('sitemap.links.resources'), description: t('sitemap.links.resourcesDesc') },
    { path: '/careers', label: t('sitemap.links.careers'), description: t('sitemap.links.careersDesc') },
    { path: '/emdr', label: t('sitemap.links.emdr'), description: t('sitemap.links.emdrDesc') },
    {
      path: '/sliding-scale',
      label: t('sitemap.links.slidingScale'),
      description: t('sitemap.links.slidingScaleDesc')
    },
    {
      path: '/groups',
      label: t('sitemap.links.groups'),
      description: t('sitemap.links.groupsDesc')
    },
    {
      path: '/locations',
      label: t('sitemap.links.locations'),
      description: t('sitemap.links.locationsDesc')
    },
    {
      path: '/modalities-rates',
      label: t('sitemap.links.modalitiesRates'),
      description: t('sitemap.links.modalitiesRatesDesc')
    },
    {
      path: '/services-investment',
      label: t('sitemap.links.servicesInvestment'),
      description: t('sitemap.links.servicesInvestmentDesc')
    },
    {
      path: '/sms-privacy-policy',
      label: t('sitemap.links.smsPrivacyPolicy'),
      description: t('sitemap.links.smsPrivacyPolicyDesc')
    },
    {
      path: '/sms-terms',
      label: t('sitemap.links.smsTerms'),
      description: t('sitemap.links.smsTermsDesc')
    },
    { path: '/sitemap', label: t('sitemap.links.sitemapPage'), description: t('sitemap.links.sitemapPageDesc') }
  ]

  // Home single-page section IDs (HomeSinglePage.jsx) — use pathname + hash for correct navigation from /pt/sitemap etc.
  const homeSections = [
    { hash: 'home', label: t('sitemap.links.homeSection'), description: t('sitemap.links.homeSectionDesc') },
    { hash: 'emdr', label: t('sitemap.links.emdrSection'), description: t('sitemap.links.emdrSectionDesc') },
    { hash: 'team', label: t('sitemap.links.teamSection'), description: t('sitemap.links.teamSectionDesc') },
    {
      hash: 'conditions-we-treat',
      label: t('sitemap.links.conditionsSection'),
      description: t('sitemap.links.conditionsSectionDesc')
    },
    { hash: 'our-office', label: t('sitemap.links.officeSection'), description: t('sitemap.links.officeSectionDesc') },
    { hash: 'faq', label: t('sitemap.links.faqSection'), description: t('sitemap.links.faqSectionDesc') },
    { hash: 'contact', label: t('sitemap.links.contactSection'), description: t('sitemap.links.contactSectionDesc') },
    {
      hash: 'contact-form',
      label: t('sitemap.links.contactFormSection'),
      description: t('sitemap.links.contactFormSectionDesc')
    }
  ]

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4"></div>
              <div className="h-4 bg-gray-300 rounded mb-2"></div>
            </div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-primary-offWhite">
        <main>
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-primary-darkGreen to-primary-green py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                  {t('sitemap.hero.title')}
                </h1>
                <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                  {t('sitemap.hero.description')}
                </p>
              </div>
            </div>
          </section>

          {/* Sitemap Content */}
          <section className="py-16 lg:py-24" aria-labelledby="sitemap-content-heading">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 id="sitemap-content-heading" className="sr-only">
                {t('sitemap.hero.title')}
              </h2>

              {/* Plain URL index — crawlable absolute links for search & AI */}
              <nav
                className="mb-16 bg-white rounded-lg shadow-md p-6 md:p-8 border border-primary-darkGreen/15"
                aria-label={t('sitemap.sections.plainIndex.title')}
              >
                <h2 className="text-2xl font-bold text-primary-darkGreen mb-2">
                  {t('sitemap.sections.plainIndex.title')}
                </h2>
                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {t('sitemap.sections.plainIndex.description')}
                </p>
                <ul className="columns-1 sm:columns-2 lg:columns-3 gap-x-8 text-sm font-mono leading-relaxed list-none p-0 m-0">
                  {mainPages.map((page) => (
                    <li key={page.path} className="mb-2 break-all">
                      <a
                        href={pageUrl(page.path)}
                        className="text-primary-darkGreen hover:underline"
                      >
                        {pageUrl(page.path)}
                      </a>
                    </li>
                  ))}
                  {homeSections.map((section) => (
                    <li key={section.hash} className="mb-2 break-all">
                      <a
                        href={`${pageUrl('/')}#${section.hash}`}
                        className="text-primary-darkGreen hover:underline"
                      >
                        {pageUrl('/')}#{section.hash}
                      </a>
                    </li>
                  ))}
                  {teamMembers.map((member, index) => {
                    const slug =
                      member.slug ||
                      (member.name || '')
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, '')
                    if (!slug) return null
                    return (
                      <li key={slug || index} className="mb-2 break-all">
                        <a
                          href={pageUrl(`/team/${slug}`)}
                          className="text-primary-darkGreen hover:underline"
                        >
                          {pageUrl(`/team/${slug}`)}
                        </a>
                      </li>
                    )
                  })}
                  {jobs.map((job) =>
                    job?.slug ? (
                      <li key={job.slug} className="mb-2 break-all">
                        <a
                          href={pageUrl(`/careers/${job.slug}`)}
                          className="text-primary-darkGreen hover:underline"
                        >
                          {pageUrl(`/careers/${job.slug}`)}
                        </a>
                      </li>
                    ) : null
                  )}
                </ul>
              </nav>

              {/* Main Pages */}
              <nav className="mb-16" aria-label={t('sitemap.sections.mainPages')}>
                <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-8 pb-4 border-b-2 border-primary-darkGreen">
                  {t('sitemap.sections.mainPages')}
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 list-none p-0 m-0">
                  {mainPages.map((page) => (
                    <li key={page.path}>
                      <Link
                        to={`${langPrefix}${page.path}`}
                        className="block h-full bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 p-6 border-l-4 border-primary-darkGreen"
                      >
                        <h3 className="text-xl font-bold text-primary-darkGreen mb-2">
                          {page.label}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {page.description}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Home Page Sections */}
              <nav className="mb-16" aria-label={t('sitemap.sections.homeSections')}>
                <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-8 pb-4 border-b-2 border-primary-darkGreen">
                  {t('sitemap.sections.homeSections')}
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 list-none p-0 m-0">
                  {homeSections.map((section) => (
                    <li key={section.hash}>
                      <Link
                        to={{ pathname: homePathname, hash: section.hash }}
                        className="block h-full bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 p-6 border-l-4 border-primary-green"
                      >
                        <h3 className="text-xl font-bold text-primary-darkGreen mb-2">
                          {section.label}
                        </h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {section.description}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Team Members */}
              {teamMembers.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-8 pb-4 border-b-2 border-primary-darkGreen">
                    {t('sitemap.sections.teamMembers')}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {teamMembers.map((member, index) => {
                      const slug =
                        member.slug ||
                        member.name
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/(^-|-$)/g, '')
                      return (
                        <Link
                          key={index}
                          to={`${langPrefix}/team/${slug}`}
                          className="block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 p-6 border-l-4 border-primary-green"
                        >
                          <h3 className="text-xl font-bold text-primary-darkGreen mb-2">
                            {member.name}
                          </h3>
                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                            {member.bio?.substring(0, 150)}...
                          </p>
                        </Link>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Career Opportunities */}
              <div className="mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-8 pb-4 border-b-2 border-primary-darkGreen">
                  {t('sitemap.sections.careers')}
                </h2>
                {jobsLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="bg-white rounded-lg shadow-md p-6 animate-pulse">
                        <div className="h-6 bg-gray-300 rounded w-2/3 mb-4"></div>
                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                      </div>
                    ))}
                  </div>
                ) : jobs.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {jobs.map((job) => (
                      <Link
                        key={job.slug}
                        to={`${langPrefix}/careers/${job.slug}`}
                        className="block bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-200 p-6 border-l-4 border-primary-green"
                      >
                        <h3 className="text-xl font-bold text-primary-darkGreen mb-2">
                          {job.title || job.slug}
                        </h3>
                        {job.area && (
                          <span className="inline-block px-3 py-1 bg-primary-darkGreen/10 text-primary-darkGreen text-sm font-medium rounded-full mb-3">
                            {job.area}
                          </span>
                        )}
                        {job.location && (
                          <p className="text-gray-600 text-sm mb-2">
                            📍 {job.location}
                          </p>
                        )}
                        {job.excerpt && (
                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                            {job.excerpt}
                          </p>
                        )}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow-md p-8 text-center">
                    <p className="text-gray-600">{t('sitemap.sections.noCareers')}</p>
                  </div>
                )}
              </div>

              {/* Local SEO Information */}
              <div className="mb-16 bg-white rounded-lg shadow-lg p-8 border-t-4 border-primary-darkGreen">
                <h2 className="text-3xl md:text-4xl font-bold text-primary-darkGreen mb-6">
                  {t('sitemap.sections.localInfo.title')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {t('sitemap.sections.localInfo.location')}
                    </h3>
                    <address className="text-gray-700 not-italic leading-relaxed">
                      <p className="font-semibold mb-2">VIVA Psychotherapy</p>
                      <p>{t('common.contact.address')}</p>
                      <p className="mt-4">
                        <strong>{t('common.contact.phoneLabel')}:</strong>{' '}
                        <a href={`tel:${t('common.contact.phone').replace(/\D/g, '')}`} className="text-primary-darkGreen hover:underline">
                          {t('common.contact.phone')}
                        </a>
                      </p>
                      <p>
                        <strong>{t('common.contact.emailLabel')}:</strong>{' '}
                        <a href={`mailto:${t('common.contact.email')}`} className="text-primary-darkGreen hover:underline">
                          {t('common.contact.email')}
                        </a>
                      </p>
                    </address>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">
                      {t('sitemap.sections.localInfo.serviceArea')}
                    </h3>
                    {isLoading ? (
                      <div className="mb-4">
                        <div className="animate-pulse space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ) : (() => {
                      const serviceAreas = translations?.sitemap?.sections?.serviceAreas
                      const cities = serviceAreas?.cities
                      
                      if (Array.isArray(cities) && cities.length > 0) {
                        return (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700 mb-4">
                            {cities.map((city, index) => (
                              <div key={index} className="flex items-center">
                                <span className="text-primary-darkGreen mr-2">•</span>
                                <span>{city}</span>
                              </div>
                            ))}
                          </div>
                        )
                      }
                      return null
                    })()}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-gray-700 font-semibold mb-2">
                        {translations?.sitemap?.sections?.serviceAreas?.counties || 'Counties Served'}
                      </p>
                      {isLoading ? (
                        <div className="flex flex-wrap gap-2">
                          <div className="h-6 w-32 bg-gray-200 rounded-full animate-pulse"></div>
                          <div className="h-6 w-32 bg-gray-200 rounded-full animate-pulse"></div>
                        </div>
                      ) : (() => {
                        const serviceAreas = translations?.sitemap?.sections?.serviceAreas
                        const counties = serviceAreas?.countyList
                        
                        if (Array.isArray(counties) && counties.length > 0) {
                          return (
                            <div className="flex flex-wrap gap-2">
                              {counties.map((county, index) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-primary-darkGreen/10 text-primary-darkGreen text-sm font-medium rounded-full"
                                >
                                  {county}
                                </span>
                              ))}
                            </div>
                          )
                        }
                        return null
                      })()}
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-primary-darkGreen font-semibold">
                        {t('sitemap.sections.localInfo.telehealth')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Links Footer */}
              <div className="bg-primary-darkGreen/5 rounded-lg p-8 text-center">
                <h2 className="text-2xl font-bold text-primary-darkGreen mb-4">
                  {t('sitemap.footer.title')}
                </h2>
                <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                  {t('sitemap.footer.description')}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    to={`${langPrefix}/contact`}
                    className="px-6 py-3 bg-primary-darkGreen text-white rounded-lg hover:bg-primary-green transition-colors duration-200 font-semibold"
                  >
                    {t('common.buttons.contactUs')}
                  </Link>
                  <Link
                    to={`${langPrefix}/`}
                    className="px-6 py-3 bg-white text-primary-darkGreen border-2 border-primary-darkGreen rounded-lg hover:bg-primary-darkGreen hover:text-white transition-colors duration-200 font-semibold"
                  >
                    {t('sitemap.footer.backToHome')}
                  </Link>
                </div>
                <p className="mt-8 text-center text-sm text-gray-500">
                  <span className="font-medium text-gray-600">{t('sitemap.footer.siteVersion')}</span>{' '}
                  <VersionBadge className="inline text-gray-500" />
                </p>
                <div className="mt-10 border-t border-gray-200 pt-8 text-center text-sm text-gray-600">
                  <h3 className="text-base font-semibold text-primary-darkGreen mb-2">
                    {t('sitemap.crawler.title')}
                  </h3>
                  <p className="mb-4 max-w-2xl mx-auto leading-relaxed">{t('sitemap.crawler.description')}</p>
                  <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2" aria-label={t('sitemap.crawler.title')}>
                    <a href="/sitemap.xml" className="text-primary-darkGreen font-medium hover:underline">
                      {t('sitemap.crawler.xmlSitemap')}
                    </a>
                    <span className="text-gray-300" aria-hidden>
                      |
                    </span>
                    <a href="/sitemap-index.txt" className="text-primary-darkGreen font-medium hover:underline">
                      {t('sitemap.crawler.plainIndex')}
                    </a>
                    <span className="text-gray-300" aria-hidden>
                      |
                    </span>
                    <a href="/robots.txt" className="text-primary-darkGreen font-medium hover:underline">
                      {t('sitemap.crawler.robotsTxt')}
                    </a>
                    <span className="text-gray-300" aria-hidden>
                      |
                    </span>
                    <a href="/llms.txt" className="text-primary-darkGreen font-medium hover:underline">
                      {t('sitemap.crawler.llmsTxt')}
                    </a>
                    <span className="text-gray-300" aria-hidden>
                      |
                    </span>
                    <a href="/llms-full.txt" className="text-primary-darkGreen font-medium hover:underline">
                      {t('sitemap.crawler.llmsFull')}
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
  )
}

export default Sitemap
